import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { usePremiumStatus } from './usePremiumStatus';

export type UsageType = 'ai_search' | 'spin_wheel' | 'quick_discover' | 'quick_discover_premium';

interface UsageLimits {
  ai_search: { free: number; premium: number };
  spin_wheel: { free: number; premium: number };
  quick_discover: { free: number; premium: number };
  quick_discover_premium: { free: number; premium: number }; // VIP feature sampling for free users
}

const USAGE_LIMITS: UsageLimits = {
  ai_search: { free: 25, premium: 100 },                  // Monthly AI search
  spin_wheel: { free: 5, premium: 50 },                   // Monthly spin wheel
  quick_discover: { free: 15, premium: 100 },             // Monthly Quick Discovery searches
  quick_discover_premium: { free: 3, premium: 100 },      // Free: 3 premium feature uses/month
};

export const useUsageLimit = (usageType: UsageType) => {
  const { user } = useAuth();
  const { isPremium } = usePremiumStatus(user?.id);
  const [currentUsage, setCurrentUsage] = useState(0);
  const [loading, setLoading] = useState(true);

  const limit = isPremium ? USAGE_LIMITS[usageType].premium : USAGE_LIMITS[usageType].free;
  const remaining = Math.max(0, limit - currentUsage);
  const percentage = (currentUsage / limit) * 100;
  const isLimitReached = currentUsage >= limit;

  const fetchUsage = useCallback(async () => {
    if (!user?.id) {
      setCurrentUsage(0);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.rpc('get_usage', {
        p_user_id: user.id,
        p_usage_type: usageType,
      });

      if (error) throw error;
      setCurrentUsage(data || 0);
    } catch (error) {
      console.error('Error fetching usage:', error);
      setCurrentUsage(0);
    } finally {
      setLoading(false);
    }
  }, [user?.id, usageType]);

  const incrementUsage = useCallback(async () => {
    if (!user?.id) return false;

    // Check limit before incrementing (enforced for ALL users, free and premium)
    if (currentUsage >= limit) {
      return false;
    }

    try {
      const { data, error } = await supabase.rpc('increment_usage', {
        p_user_id: user.id,
        p_usage_type: usageType,
      });

      if (error) throw error;
      
      setCurrentUsage(data || currentUsage + 1);
      return true;
    } catch (error) {
      console.error('Error incrementing usage:', error);
      return false;
    }
  }, [user?.id, usageType, currentUsage, limit, isPremium]);

  const trackPromptView = useCallback(async (location: string) => {
    if (!user?.id) return;

    try {
      await supabase.from('premium_prompt_views').insert({
        user_id: user.id,
        prompt_location: location,
      });
    } catch (error) {
      console.error('Error tracking prompt view:', error);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  return {
    currentUsage,
    limit,
    remaining,
    percentage,
    isLimitReached,
    isPremium,
    loading,
    incrementUsage,
    trackPromptView,
    refetch: fetchUsage,
  };
};
